import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  updateTopupcardProvider,
  fetchTopupcardProviders,
  resetTopupcardProviderState,
} from "../../../../features/auth/store/topupcardProviderSlice";
import Modal from "../../../../components/Reusable/Modal";
import RichTextEditor from "../../../../components/Reusable/RichTextEditor";
import { EditProps } from "../types";
import { TopupcardProvider } from "../../../../features/auth/store/types/topup-types";

export const EditTopupProviderModal: React.FC<EditProps> = ({
  id: providerId,
  isOpen,
  onClose,
}) => {
  const { topupcardProviders, loading, currentPage } = useAppSelector(
    (state) => state.topupcardProviders,
  );
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<{
    name: string;
    slug: string;
    description: string;
    status: "on" | "off";
    index: number;
    search_tags: string;
    out_of_stock: boolean;
    hidden_on_ios: boolean;
    recipient_label: string;
    verify_recipient: boolean;
    additional_input: boolean;
    additional_input_placeholder: string;
    additional_input_type: string;
    additional_input_label: string;
    additional_input_form_name: string;
  }>({
    name: "",
    index: 1,
    search_tags: "",
    slug: "",
    description: "",
    status: "on",
    out_of_stock: false,
    hidden_on_ios: false,
    recipient_label: "",
    verify_recipient: false,
    additional_input: false,
    additional_input_placeholder: "",
    additional_input_type: "",
    additional_input_label: "",
    additional_input_form_name: "",
  });
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && topupcardProviders.length === 0) {
      dispatch(fetchTopupcardProviders({}));
    }
  }, [dispatch, isOpen, topupcardProviders.length]);

  useEffect(() => {
    if (!isOpen || providerId === null) {
      dispatch(resetTopupcardProviderState());
      return;
    }

    const providerToEdit = topupcardProviders.find(
      (provider: TopupcardProvider) => provider.id === providerId,
    );

    if (providerToEdit) {
      setForm({
        name: providerToEdit.name || "",
        index: providerToEdit.index ?? 1,
        search_tags: Array.isArray(providerToEdit.search_tags)
          ? providerToEdit.search_tags.join(", ")
          : providerToEdit.search_tags || "",
        slug: providerToEdit.slug || "",
        description: providerToEdit.description || "",
        status: providerToEdit.status || "on",
        out_of_stock: Boolean(providerToEdit.out_of_stock),
        hidden_on_ios: Boolean(providerToEdit.hidden_on_ios),
        recipient_label: providerToEdit.recipient_label || "",
        verify_recipient: Boolean(providerToEdit.verify_recipient),
        additional_input: Boolean(providerToEdit.additional_input),
        additional_input_placeholder:
          providerToEdit.additional_input_placeholder || "",
        additional_input_type: providerToEdit.additional_input_type || "",
        additional_input_label: providerToEdit.additional_input_label || "",
        additional_input_form_name:
          providerToEdit.additional_input_form_name || "",
      });
      setCurrentImageUrl(providerToEdit.image_url || null);
      setImage(null);
    } else if (!loading && topupcardProviders.length > 0) {
      console.warn(`Provider with ID ${providerId} not found in the list.`);
    }
  }, [isOpen, providerId, topupcardProviders, loading, dispatch]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    if (e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
      setForm({ ...form, [e.target.name]: e.target.checked });
      return;
    }
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => setImage(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (providerId === null) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("slug", form.slug);
      formData.append("description", form.description);
      formData.append("status", form.status);
      formData.append("index", form.index.toString());
      formData.append("out_of_stock", form.out_of_stock ? "1" : "0");
      formData.append("hidden_on_ios", form.hidden_on_ios ? "1" : "0");
      const tagsArray = form.search_tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
      if (tagsArray.length > 0) {
        tagsArray.forEach((tag) => {
          formData.append("search_tags[]", tag);
        });
      } else {
        // Pass an empty string so Laravel knows search_tags was explicitly cleared
        formData.append("search_tags", "");
      }
      formData.append("recipient_label", form.recipient_label);
      formData.append("verify_recipient", form.verify_recipient ? "1" : "0");
      formData.append("additional_input", form.additional_input ? "1" : "0");
      formData.append(
        "additional_input_placeholder",
        form.additional_input_placeholder,
      );
      formData.append("additional_input_type", form.additional_input_type);
      formData.append("additional_input_label", form.additional_input_label);
      formData.append(
        "additional_input_form_name",
        form.additional_input_form_name,
      );
      formData.append("_method", "PUT"); // Laravel method spoofing

      if (image) {
        formData.append("image", image);
      }

      await dispatch(
        updateTopupcardProvider({ id: providerId, data: formData as any }),
      ).unwrap();

      toast.success("Top up Provider updated successfully! 🚀");
      onClose();
      // Refresh the list to show updated data
      await dispatch(fetchTopupcardProviders({ page: currentPage }));
    } catch (err: any) {
      toast.error(err?.message || "Failed to update provider");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto"
    >
      <div className="max-h-[90vh] overflow-y-auto pr-1">
        <h2 className="text-lg md:text-xl font-bold mb-6">
          Edit Top up Provider
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-2">Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded dark:bg-white/10"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Slug</label>
            <input
              type="text"
              name="slug"
              value={form.slug}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded dark:bg-white/10"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-white/10"
            >
              <option value="on" className="dark:text-black">
                On
              </option>
              <option value="off" className="dark:text-black">
                Off
              </option>
            </select>
          </div>
          <div className="mb-4 flex items-center gap-2">
            <input
              id="out_of_stock"
              type="checkbox"
              name="out_of_stock"
              checked={form.out_of_stock}
              onChange={handleChange}
            />
            <label htmlFor="out_of_stock">Out of Stock</label>
          </div>
          <div className="mb-4 flex items-center gap-2">
            <input
              id="hidden_on_ios"
              type="checkbox"
              name="hidden_on_ios"
              checked={form.hidden_on_ios}
              onChange={handleChange}
            />
            <label htmlFor="hidden_on_ios">Hide on iOS app</label>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Index Order</label>
            <input
              type="number"
              name="index"
              value={form.index}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-white/10"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Search Tags (Comma Separated)</label>
            <input
              type="text"
              name="search_tags"
              value={form.search_tags}
              onChange={handleChange}
              placeholder="e.g. mtn, airtime, data"
              className="w-full p-2 border rounded dark:bg-white/10"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Recipient Label</label>
            <input
              type="text"
              name="recipient_label"
              value={form.recipient_label}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-white/10"
            />
          </div>
          <div className="mb-4 flex items-center gap-2">
            <input
              id="verify_recipient"
              type="checkbox"
              name="verify_recipient"
              checked={form.verify_recipient}
              onChange={handleChange}
            />
            <label htmlFor="verify_recipient">Verify Recipient</label>
          </div>
          <div className="mb-4 flex items-center gap-2">
            <input
              id="additional_input"
              type="checkbox"
              name="additional_input"
              checked={form.additional_input}
              onChange={handleChange}
            />
            <label htmlFor="additional_input">Additional Input</label>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Additional Input Placeholder</label>
            <input
              type="text"
              name="additional_input_placeholder"
              value={form.additional_input_placeholder}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-white/10"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Additional Input Type</label>
            <input
              type="text"
              name="additional_input_type"
              value={form.additional_input_type}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-white/10"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Additional Input Label</label>
            <input
              type="text"
              name="additional_input_label"
              value={form.additional_input_label}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-white/10"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Additional Input Form Name</label>
            <input
              type="text"
              name="additional_input_form_name"
              value={form.additional_input_form_name}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-white/10"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Description</label>
            <RichTextEditor
              value={form.description}
              onChange={(value) => setForm({ ...form, description: value })}
              placeholder="Write provider description..."
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Image</label>

            {/* Show current image if exists and no new image selected */}
            {currentImageUrl && !image && (
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-2 dark:text-white">
                  Current Image:
                </p>
                <div className="relative rounded-md overflow-hidden h-20 w-20">
                  <img
                    src={currentImageUrl}
                    alt="Current provider"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center dark:bg-white/10 dark:text-white">
              <input
                type="file"
                id="providerImageUpload"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="providerImageUpload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mb-2"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 8L12 3L7 8"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600">
                  {currentImageUrl && !image
                    ? "Upload new image"
                    : "Upload from computer"}
                </span>
              </label>
            </div>

            {/* Show new image preview */}
            {image && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="relative rounded-md overflow-hidden h-20">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="New provider"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-1 right-1 bg-white rounded-full p-1"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full bg-gradient-to-r from-[#027EC2] to-[#039EF2] h-10 text-white rounded ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Updating..." : "Update Provider"}
          </button>
        </form>
      </div>
    </Modal>
  );
};
